import Icon from '@/components/Icon'

export interface CardHeadlineProps {
	title: string
	isExternal: boolean
}

/**
 * Card headline, with the external-link icon trailing the title inline (per design).
 *
 * The icon is a plain inline-block in normal text flow rather than a flex item, so it
 * follows the last word of the title instead of being pushed to the right-hand edge.
 * The last word and the icon are kept in a `whitespace-nowrap` span so the icon can
 * never be orphaned onto a line of its own when the title fills the final line.
 *
 * `align-[-0.125em]` optically centres the icon on the title's cap band. The glyph fills
 * its whole 1em box, so `align-middle` leaves it hanging ~0.24em below the baseline while
 * its top only just reaches the cap — it reads as sitting too low. Placing the bottom edge
 * 0.125em below the baseline puts the icon's centre exactly on the cap-band centre
 * (cap height is ~0.75em, so cap centre sits 0.375em above the baseline and the icon's
 * centre sits 0.5em above its bottom edge).
 */
export function CardHeadline({ title, isExternal }: CardHeadlineProps) {
	if (!isExternal) {
		return <span>{title}</span>
	}

	// Split off the last word so it can travel with the icon. Sliced rather than
	// split/joined so the rest of the title keeps its original whitespace.
	const trimmed = title.trimEnd()
	const lastSpaceIndex = trimmed.lastIndexOf(' ')
	const leadingWords =
		lastSpaceIndex === -1 ? '' : trimmed.slice(0, lastSpaceIndex + 1)
	const lastWord =
		lastSpaceIndex === -1 ? trimmed : trimmed.slice(lastSpaceIndex + 1)

	return (
		<span>
			{leadingWords}
			<span className="whitespace-nowrap">
				{lastWord}
				<Icon
					icon="fa-solid:external-link-alt"
					className="ml-2 inline-block align-[-0.125em]"
					width="1em"
					height="1em"
				/>
			</span>
		</span>
	)
}
